import { useQueryClient } from "@tanstack/react-query"
import axios from "axios"
import { useCallback, useRef } from "react"
import { DefaultValues, UseFormReturn } from "react-hook-form"
import { WorkflowResponseDataSchema } from "schema"
import { z } from "zod"
import { BuilderFormValues, flatToWorkflows } from "../components/Builder/lib/workflowsCanvas"
import { getWorkflowsQueryKey } from "../lib/workflowsQuery"

type Workflow = z.infer<typeof WorkflowResponseDataSchema>

function snapshot(form: UseFormReturn<BuilderFormValues>) {
  return structuredClone(form.getValues()) as BuilderFormValues
}

export function useWorkflowsCanvasSave(
  form: UseFormReturn<BuilderFormValues>,
  workflows: Workflow[],
  campaignId?: string,
) {
  const queryClient = useQueryClient()
  const lastSavedRef = useRef<BuilderFormValues>(snapshot(form))

  const flush = useCallback(async () => {
    const drafts = flatToWorkflows(form.getValues("workflows") ?? [], form.getValues("nodes") ?? [])
    const keptIds = new Set<string>()

    for (const draft of drafts) {
      let workflowId = draft.persistedId
      if (!workflowId) {
        const created = await axios
          .post<{ data: { id: string } }>("/workflows", {
            name: draft.name ?? undefined,
            campaignId,
          })
          .then((response) => response.data.data)
        workflowId = created.id
      }
      keptIds.add(workflowId)
      await axios.put(`/workflows/${workflowId}`, {
        name: draft.name,
        isEnabled: draft.isEnabled,
        nodes: draft.nodes,
      })
    }

    for (const workflow of workflows) {
      if (!keptIds.has(workflow.id)) await axios.delete(`/workflows/${workflow.id}`)
    }

    await queryClient.invalidateQueries({ queryKey: getWorkflowsQueryKey(campaignId) })
    lastSavedRef.current = snapshot(form)
  }, [form, workflows, campaignId, queryClient])

  const sync = useCallback(
    (nextDefaults: DefaultValues<BuilderFormValues>) => {
      const current = snapshot(form)
      if (JSON.stringify(current) !== JSON.stringify(lastSavedRef.current)) return
      form.reset(nextDefaults)
      lastSavedRef.current = snapshot(form)
    },
    [form],
  )

  return { flush, sync }
}
